<template>
  <div class="withdraw-confirmation">
    <!-- Summary Card -->
    <div class="withdraw-confirmation__card">
      <div class="withdraw-confirmation__row">
        <span class="withdraw-confirmation__label">Amount</span>
        <span class="withdraw-confirmation__value">
          {{ formatAmountToDollar(payloadDetails.tokens, 2, payloadDetails.currency) }}
        </span>
      </div>
      <div class="withdraw-confirmation__row">
        <span class="withdraw-confirmation__label">Transaction Fee</span>
        <span class="withdraw-confirmation__value">{{ charges }}</span>
      </div>
    </div>

    <!-- Total Section -->
    <div class="withdraw-confirmation__total">
      <div class="withdraw-confirmation__total-row">
        <span class="withdraw-confirmation__total-label">You will receive</span>
        <span class="withdraw-confirmation__total-value">{{ amount }}</span>
      </div>
    </div>

    <!-- PIN Form -->
    <form @submit.prevent="withdrawRequest" class="withdraw-confirmation__form">
      <h3 class="withdraw-confirmation__pin-title">Enter PIN to confirm</h3>
      <div ref="codeInput" class="withdraw-confirmation__pin-inputs">
        <OnboardingCodeInput
          label="Verification code"
          type="password"
          :digit="digit.firstDigit"
          :error="errorOne || {}"
          :position="1"
          @input="setDigit"
        />
        <div class="withdraw-confirmation__separator">&ndash;</div>
        <OnboardingCodeInput
          label="Verification code"
          type="password"
          :digit="digit.secondDigit"
          :error="errorTwo || {}"
          :position="2"
          @input="setDigit"
        />
        <div class="withdraw-confirmation__separator">&ndash;</div>
        <OnboardingCodeInput
          label="Verification code"
          type="password"
          :digit="digit.thirdDigit"
          :error="errorThree || {}"
          :position="3"
          @input="setDigit"
        />
        <div class="withdraw-confirmation__separator">&ndash;</div>
        <OnboardingCodeInput
          label="Verification code"
          type="password"
          :digit="digit.forthDigit"
          :error="errorFour || {}"
          :position="4"
          @input="setDigit"
        />
      </div>
      <button
        type="submit"
        class="withdraw-confirmation__btn"
        :disabled="loading || isClicked"
      >
        <span v-if="loading || isClicked" class="withdraw-confirmation__spinner"></span>
        <span v-else>Confirm Withdrawal</span>
      </button>
    </form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import useValidate from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import OnboardingCodeInput from "@/components/onboarding/OnboardingCodeInput.vue";
import { formatAmountToDollar } from "@/utils/helpers.js";
import { getItem } from "@/utils/storage.js";

export default {
  name: "WithdrawConfirmationForm",

  components: {
    OnboardingCodeInput,
  },

  emits: ["withdraw"],

  props: {
    loading: { type: Boolean, default: () => false, required: true },
    fees: { type: Number, default: () => 0, required: true },
  },

  data() {
    return {
      v$: useValidate(),
      fields: [],
      fieldNum: 0,
      payload: { pin: "" },
      payloadDetails: {},
      charges: "",
      amount: "",
      digit: {
        firstDigit: "",
        secondDigit: "",
        thirdDigit: "",
        forthDigit: "",
      },
      isClicked: false,
      errorOne: {},
      errorTwo: {},
      errorThree: {},
      errorFour: {},
    };
  },

  validations() {
    return {
      digit: {
        firstDigit: { required, maxLength: maxLength(1) },
        secondDigit: { required, maxLength: maxLength(1) },
        thirdDigit: { required, maxLength: maxLength(1) },
        forthDigit: { required, maxLength: maxLength(1) },
      },
    };
  },

  mounted() {
    if (!getItem("withdrawalPayload")) return;
    this.payloadDetails = getItem("withdrawalPayload");
    const currency = this.payloadDetails?.currency;
    this.amount = formatAmountToDollar(
      this.payloadDetails?.amount - this.fees,
      2,
      currency
    );
    this.charges = formatAmountToDollar(this.fees, 2, currency);
    this.fields = this.$refs.codeInput.querySelectorAll("input");
    this.fieldNum = this.fields.length;
  },

  methods: {
    ...mapActions("organisationModule", ["verifyPin"]),

    formatAmountToDollar,

    async withdrawRequest() {
      this.isClicked = true;
      const response = await this.verifyPin(this.payload);
      if (response) {
        this.$emit("withdraw", this.payloadDetails);
      }
      this.isClicked = false;
    },

    setDigit(payload) {
      const keyCode = payload.event ? payload.event.inputType : null;
      switch (payload.position) {
        case 1:
          this.digit.firstDigit = payload.digit;
          this.v$.digit.firstDigit.$touch();
          this.errorOne = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            return;
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 2:
          this.digit.secondDigit = payload.digit;
          this.v$.digit.secondDigit.$touch();
          this.errorTwo = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 3:
          this.digit.thirdDigit = payload.digit;
          this.v$.digit.thirdDigit.$touch();
          this.errorThree = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 4:
          this.digit.forthDigit = payload.digit;
          this.v$.digit.forthDigit.$touch();
          this.errorFour = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          }
          break;

        default:
          break;
      }
    },

    concatDigits() {
      this.payload.pin = this.digit.firstDigit
        .concat(this.digit.secondDigit)
        .concat(this.digit.thirdDigit)
        .concat(this.digit.forthDigit);
    },
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #6366F1;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.withdraw-confirmation {
  padding: 8px 0;

  &__card {
    background: $bg-subtle;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 13px;
    color: $text-secondary;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child) {
      border-bottom: 1px solid $border-color;
    }
  }

  &__label {
    color: $text-muted;
  }

  &__value {
    font-weight: 500;
    color: $text-primary;
  }

  &__total {
    background: $bg-subtle;
    border-radius: 10px;
    padding: 20px 16px;
    margin-bottom: 32px;
    text-align: center;
  }

  &__total-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  &__total-label {
    font-size: 13px;
    color: $text-muted;
  }

  &__total-value {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
  }

  &__form {
    width: 100%;
  }

  &__pin-title {
    font-size: 14px;
    font-weight: 500;
    color: $text-secondary;
    text-align: center;
    margin: 0 0 20px;
  }

  &__pin-inputs {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;
  }

  &__separator {
    color: $text-muted;
    padding: 0 4px;
  }

  &__btn {
    width: 100%;
    height: 48px;
    background: $btn-primary;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      background: $border-color;
      color: $text-muted;
      cursor: not-allowed;
    }
  }

  &__spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
